/**
 * MermaidControl
 * Renders Mermaid diagram definitions.
 * Lazily loads mermaid via CDN script tag on first use.
 * Security: All SVG output is sanitized using DOMPurify.
 * Performance: Memoized to prevent unnecessary re-renders
 */

import React, { useEffect, useRef, useId, memo } from 'react';
import DOMPurify from 'dompurify';
import type { ControlRendererProps } from '../ControlRenderer';

/**
 * Configure DOMPurify to allow safe SVG content from mermaid
 */
const SVG_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['svg', 'g', 'path', 'text', 'tspan', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse', 'defs', 'marker', 'pattern', 'style', 'title', 'desc'],
  ALLOWED_ATTR: ['viewBox', 'width', 'height', 'x', 'y', 'd', 'stroke', 'fill', 'cx', 'cy', 'r', 'x1', 'y1', 'x2', 'y2', 'points', 'rx', 'ry', 'class', 'id', 'style', 'xmlns', 'xmlns:xlink'],
  KEEP_CONTENT: true,
};

let mermaidLoaded = false;

function ensureMermaid(): Promise<void> {
  if (mermaidLoaded || (window as any).mermaid) {
    mermaidLoaded = true;
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';
    script.onload = () => {
      (window as any).mermaid?.initialize({ startOnLoad: false });
      mermaidLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export const MermaidControl = memo((props: ControlRendererProps) => {
  const { control } = props;
  const definition = ((control as any).definition ?? (control as any).content ?? '') as string;
  const containerRef = useRef<HTMLDivElement>(null);
  const uid = useId().replace(/:/g, '');

  useEffect(() => {
    if (!definition || !containerRef.current) return;
    let cancelled = false;

    ensureMermaid()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const mermaid = (window as any).mermaid;
        mermaid
          .render(`mermaid-${uid}`, definition)
          .then(({ svg }: { svg: string }) => {
            if (!cancelled && containerRef.current) {
              // Sanitize SVG output to prevent XSS vulnerabilities
              const sanitizedSvg = DOMPurify.sanitize(svg, SVG_SANITIZE_CONFIG);
              containerRef.current.innerHTML = sanitizedSvg;
            }
          });
      })
      .catch(() => {
        if (!cancelled && containerRef.current) {
          containerRef.current.textContent = 'Failed to load diagram renderer.';
        }
      });

    return () => {
      cancelled = true;
    };
  }, [definition, uid]);

  return (
    <div className="af-mermaid-control" data-control-id={control.id}>
      {control.label && <p className="af-mermaid-control__label">{control.label}</p>}
      {definition ? (
        <div ref={containerRef} className="af-mermaid-control__diagram" />
      ) : (
        <div className="af-mermaid-control--empty">No diagram definition</div>
      )}
    </div>
  );
});

MermaidControl.displayName = 'MermaidControl';

export default MermaidControl;
